import { getApiBase } from './utils'

type PublicCfg = {
  enabled: boolean
  cookie_name: string
  cookie_max_age_seconds: number
}

let cached: PublicCfg | null = null
let inflight: Promise<void> | null = null

const FALLBACK: PublicCfg = {
  enabled: true,
  cookie_name: 'totemium_ref',
  cookie_max_age_seconds: 90 * 24 * 60 * 60,
}

/** Extrait ?ref= depuis la query ou depuis un fragment type #/path?ref= (certains liens / redirections). */
function getRefParamFromWindow(): string | null {
  try {
    const q = new URLSearchParams(window.location.search)
    const fromSearch = q.get('ref')?.trim()
    if (fromSearch) return fromSearch
    const hash = window.location.hash || ''
    const qi = hash.indexOf('?')
    if (qi >= 0) {
      const hq = new URLSearchParams(hash.slice(qi + 1))
      return hq.get('ref')?.trim() ?? null
    }
  } catch {
    /* ignore */
  }
  return null
}

function isValidAffiliateRef(trimmed: string): boolean {
  return /^[a-f0-9]{8}$/i.test(trimmed) || /^[a-z0-9]{4,32}$/i.test(trimmed)
}

/**
 * Charge la config publique (cookie, durée) depuis l’API — alignée sur la DB admin.
 */
export function ensureAffiliatePublicConfig(): Promise<void> {
  if (cached) return Promise.resolve()
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const base = getApiBase().replace(/\/$/, '')
      const r = await fetch(`${base}/affiliate/public-config`)
      if (r.ok) {
        const j = (await r.json()) as Partial<PublicCfg>
        cached = {
          enabled: j.enabled !== false,
          cookie_name: (j.cookie_name || FALLBACK.cookie_name).trim() || FALLBACK.cookie_name,
          cookie_max_age_seconds:
            typeof j.cookie_max_age_seconds === 'number' && j.cookie_max_age_seconds >= 60
              ? j.cookie_max_age_seconds
              : FALLBACK.cookie_max_age_seconds,
        }
        return
      }
    } catch {
      /* use fallback */
    }
    cached = { ...FALLBACK }
  })()
  return inflight
}

/**
 * Si URL contient ?ref=, pose le cookie first-touch (nom et max-age depuis l’API).
 * Envoie la vue affilié tout de suite (sans attendre le réseau) pour éviter de perdre ?ref= lors d’une redirection (ex. PWA).
 */
export async function captureReferralFromUrl(): Promise<void> {
  const raw = getRefParamFromWindow()
  const trimmed = raw ? raw.trim() : ''
  if (trimmed && isValidAffiliateRef(trimmed)) {
    reportAffiliateLinkViewOnce(trimmed)
  }

  await ensureAffiliatePublicConfig()
  const pub = cached ?? FALLBACK
  if (!pub.enabled) return
  if (!trimmed || !isValidAffiliateRef(trimmed)) return
  try {
    const v = encodeURIComponent(trimmed.toLowerCase())
    document.cookie = `${pub.cookie_name}=${v};path=/;max-age=${pub.cookie_max_age_seconds};SameSite=Lax`
  } catch {
    /* ignore */
  }
}

/**
 * Envoie une fois par session navigateur une vue du lien ?ref= vers l’API (stats dashboard affilié).
 * Pas d’auth ; best-effort (ignore les erreurs réseau).
 * Le POST part sans attendre /affiliate/public-config : le backend ignore si le programme est désactivé.
 */
export function reportAffiliateLinkViewOnce(refRaw: string): void {
  const ref = refRaw.trim().toLowerCase()
  if (!ref) return
  const key = `affiliate_lv_${ref}`
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key)) return
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1')
  } catch {
    /* private mode: on tente quand même un envoi */
  }
  void (async () => {
    try {
      const base = getApiBase().replace(/\/$/, '')
      const params = new URLSearchParams(window.location.search)
      const hash = window.location.hash || ''
      const qi = hash.indexOf('?')
      if (qi >= 0) {
        const hq = new URLSearchParams(hash.slice(qi + 1))
        hq.forEach((v, k) => {
          if (!params.has(k)) params.set(k, v)
        })
      }
      const path = `${window.location.pathname}${window.location.search}`
      await fetch(`${base}/affiliate/track-link-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ref,
          referrer_url: document.referrer || null,
          landing_path: path.length > 512 ? path.slice(0, 512) : path,
          utm_source: params.get('utm_source'),
          utm_medium: params.get('utm_medium'),
          utm_campaign: params.get('utm_campaign'),
        }),
      })
    } catch {
      /* ignore */
    }
  })()
}

export function getReferralCookie(): string | null {
  const pub = cached ?? FALLBACK
  try {
    const parts = document.cookie.split(';')
    for (const p of parts) {
      const [k, ...rest] = p.trim().split('=')
      if (k === pub.cookie_name && rest.length) return decodeURIComponent(rest.join('='))
    }
  } catch {
    /* ignore */
  }
  return null
}

/** @deprecated Utiliser ensureAffiliatePublicConfig + captureReferralFromUrl */
export const REFERRAL_COOKIE_NAME = 'totemium_ref'
